/* eslint-disable */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { ArrowClockwise } from 'react-bootstrap-icons';
import { useTheme } from '@mui/styles';
import { SORTKEYS } from '../../constants/Orders';
import { useTranslation } from 'react-i18next';
import { TABLE_SETTING } from '../../constants/Tables';
import { useCurrentUser } from '../../authentication/useAuth';
import { useSessionState } from '../../hooks/useSessionState';
import usePersistentTableColumns from '../../hooks/usePersistentTableColumns';
import ColumnCustomizer from '../../components/OrdersTable/components/ColumnCustomizer';
import DataExport from '../../components/OrdersTable/components/DataExport';
import OrderFilters, {
	DefaultFiltering,
} from '../../components/OrdersTable/components/OrderFilters';
import OrdersTable from '../../components/OrdersTable';
import columnConfig, {
	columnOrder as defaultColumnOrder,
	columnVisibility as defaultColumnVisibility,
} from '../../components/OrdersTable/columnConfig';
import useIsMountedRef from '../../hooks/useIsMountedRef';
import Error from '../../components/Error';
import Success from '../../components/Success';
import ClearButton from '../../components/ClearButton';
import { useOrdersApi } from '../../apis';
import NewOrderDrawer from '../../components/OrdersTable/components/NewOrderDrawer';
import {
	ORDER_LIST_RESPONSE_FIELDS,
	normalizeOrderListPayload,
} from '../../apis/hooks/models/orderList';

/** @typedef {import('../../apis/hooks/models/orderList').OrdersListPayload} OrdersListPayload */

function Orders() {
	const isMountedRef = useIsMountedRef();
	const theme = useTheme();
	const currentUser = useCurrentUser();
	const canOrder = Boolean(currentUser?.canOrder);
	const [orders, setOrders] = useState(
		/** @type {OrdersListPayload | null} */ (null)
	);
	const [success, setSuccess] = useState(null);
	const [error, setError] = useState(null);
	const { t } = useTranslation('download');
	const [{ limit, page }, setPageParams] = useSessionState(
		'orders_page_params',
		{ limit: TABLE_SETTING.DEFAULT_PAGE_SIZE, page: 0 }
	);
	const [filters, setFilters] = useSessionState(
		'orders_filters',
		DefaultFiltering
	);
	const [sorts, setSorts] = useSessionState('orders_sorts', []);
	const openFiltersRef = useRef(null);
	const {
		columnOrder,
		setColumnOrder,
		columnVisibility,
		setColumnVisibility,
		resetColumns,
	} = usePersistentTableColumns({
		storageKeyPrefix: 'orders',
		defaultColumnOrder,
		defaultColumnVisibility,
	});
	const [isLoading, setIsLoading] = useState(false);
	const { state } = useLocation();
	const isFilterOpen = state?.isFilterOpen;
	const { getOrderList } = useOrdersApi();

	useEffect(() => {
		const [isOpen, setIsOpen] = openFiltersRef.current;
		setIsOpen(!!isFilterOpen);
	}, [isFilterOpen]);

	useEffect(() => {
		const isVisible = (key) => Object.keys(columnVisibility).includes(key);
		if (sorts.length && !sorts.every(({ key }) => isVisible(key))) {
			// if it's not in the columnVisibility it should be removed from sorts
			setSorts(sorts.filter(({ key }) => isVisible(key)));
		}
	}, [sorts, columnVisibility, setSorts]);

	const sort = useCallback(() => {
		if (sorts.length) {
			return sorts
				.map(
					({ key, direction }) =>
						`${SORTKEYS[key] || key} ${direction.toUpperCase()}`
				)
				.join(', ');
		} else if (
			(filters.status === undefined && !sorts.length) ||
			(Object.keys(filters.status).every((k) => !filters.status[k]) &&
				!sorts.length &&
				filters.status !== undefined)
		) {
			return 'Order_Date DESC';
		}
		// this is the default, we will add or change sort fields later
		return 'Order_Date DESC';
	}, [sorts, filters]);

	const fetchOrders = useCallback(async () => {
		setIsLoading(true);

		const response = await getOrderList({
			limit,
			page,
			filters,
			sort: sort(),
		});

		if (response?.isAborted) {
			setIsLoading(false);
			return;
		}

		if (response?.isError) {
			console.log(response.error || 'failed to load orders');
			setIsLoading(false);
			return;
		}

		if (isMountedRef.current) {
			setOrders(normalizeOrderListPayload(response?.data));
		}

		setIsLoading(false);
	}, [isMountedRef, sort, filters, limit, page, getOrderList]);

	useEffect(() => {
		fetchOrders();
		localStorage.getItem('success');
		window.addEventListener('storage', (e) => {
			setSuccess(localStorage.getItem('success'));
		});
		localStorage.getItem('error');
		window.addEventListener('storage', (e) => {
			setError(localStorage.getItem('error'));
		});
	}, [fetchOrders]);

	return (
		<OrdersTable
			orders={orders?.[ORDER_LIST_RESPONSE_FIELDS.ORDERS] || []}
			total={orders?.[ORDER_LIST_RESPONSE_FIELDS.COUNT] || 0}
			sorts={sorts}
			onSort={setSorts}
			onPageChange={setPageParams}
			pagination={{ limit, page }}
			filters={filters}
			onFilter={setFilters}
			columnOrder={columnOrder}
			columnVisibility={columnVisibility}
			onColumnOrderChange={setColumnOrder}
			onColumnVisibilityChange={setColumnVisibility}
			columnConfig={columnConfig}
			isLoading={isLoading}
			aria-label="Orders Table"
			role="rowgroup"
			tabIndex={0}
		>
			<Box
				inputpros={{
					'aria-label': 'Error and Success messages',
					role: 'row',
				}}
				component="div"
				sx={{
					display: { xs: 'none', sm: 'block' },
					width: '100%',
					marginLeft: '-.65rem',
				}}
			>
				{error && !success && <Error message={t('error')} />}

				{success && <Success message={t('success')} />}
			</Box>
			{!!sorts.length > 0 && (
				<Box
					inputpros={{
						'aria-label': 'order filters',
						role: 'row',
					}}
					component="div"
					sx={{
						display: 'flex',
						alignItems: 'right',
						justifyContent: 'flex-end',
						margin: {
							xs: '.5rem .2rem auto -.2rem',
							sm: '.1rem .65rem .5rem .5rem',
						},
					}}
				>
					<ClearButton
						onChange={setSorts}
						label="clear_sort"
						icon={
							<ArrowClockwise
								style={{
									fontSize: theme.palette.textSize.sizeXs,
									color: theme.palette.link.main,
								}}
							/>
						}
					/>
				</Box>
			)}

			<Box
				inputpros={{
					'aria-label': 'order filters',
					role: 'row',
				}}
				component="div"
				sx={{
					display: 'inline',
					width: '6rem',
					margin: {
						xs: '.5rem .2rem auto -.2rem',
						sm: '.1rem .65rem .5rem .5rem',
					},
				}}
			>
				<OrderFilters
					inputpros={{
						'aria-label': 'order filters',
						role: 'cell',
					}}
					onFilter={(_filters) => {
						setFilters(_filters);
						setPageParams({ page: 0, limit });
					}}
					value={filters}
					dataTestId="orders_table_filter"
					openRef={(ref) => {
						openFiltersRef.current = ref;
					}}
				/>
			</Box>
			<Box
				inputpros={{
					'aria-label': 'column customizer',
					role: 'row',
				}}
				component="div"
				sx={{
					display: 'inline',
					width: '9.25rem',
					margin: {
						xs: '.5rem .2rem auto -.2rem',
						sm: '.1rem .65rem .5rem .5rem',
					},
				}}
			>
				<ColumnCustomizer
					inputpros={{
						'aria-label': 'column customizer',
						role: 'cell',
					}}
					order={columnOrder}
					visibility={columnVisibility}
					onOrderChange={setColumnOrder}
					onVisibilityChange={setColumnVisibility}
					onReset={resetColumns}
					columnConfig={columnConfig}
					dataTestId="orders_table_column_customize"
				/>
			</Box>

			<Box
				inputpros={{
					'aria-label': 'data export',
					role: 'row',
				}}
				component="div"
				sx={{
					display: 'inline',
					ml: '1.5rem',
					width: '9.25rem',
					minWidth: {
						xs: '90px',
						sm: '115px',
					},
				}}
			>
				<DataExport
					inputpros={{
						'aria-label': 'data export',
						role: 'cell',
					}}
					tabIndex={0}
					isLoading={isLoading}
					filters={filters}
					columnOrder={columnOrder}
					columnVisibility={columnVisibility}
					dataTestId="orders_table_export"
					onVisibilityChange={setColumnVisibility}
					sort={sort()}
					onReset={resetColumns}
				/>
			</Box>
			<Box
				inputpros={{
					'aria-label': 'new order',
					role: 'row',
				}}
				component="div"
				sx={{
					display: 'inline-flex',
					ml: '1.5rem',
					width: 'auto',
					minWidth: 'max-content',
				}}
			>
				<NewOrderDrawer
					inputpros={{
						'aria-label': 'new order',
						role: 'cell',
					}}
					canOrder={canOrder}
					dataTestId="orders_table_new_order_drawer"
				/>
			</Box>
		</OrdersTable>
	);
}

export default Orders;
